import { useCallback, useEffect, useState } from "react";

import { Calendar } from "primereact/calendar";
import { Button } from "primereact/button";

import { DateRange } from "./hooks/useTransactionsExplorer";

type Draft = (Date | null)[];

const sameDay = (a: Date, b: Date) => a.getTime() === b.getTime();

interface TransactionsPeriodSelectorProps {
  appliedRange: DateRange;
  applyPeriod: (period: DateRange) => void;
  clearPeriod: () => void;
  disabled?: boolean;
}

/**
 * Date range picker that always shows the range the table is filtered by (CLR-278).
 *
 * The calendar holds a draft only while the user is picking: a completed range applies
 * immediately (the overlay closes on the second click), an incomplete one reverts to the
 * applied range when the overlay closes, and the draft re-syncs whenever the applied
 * dates change. It is never cleared to "Select date range" while a range is applied.
 */
export const TransactionsPeriodSelector = ({
  appliedRange,
  applyPeriod,
  clearPeriod,
  disabled = false,
}: TransactionsPeriodSelectorProps) => {
  // Keyed on the dates, not the object identity: the draft follows every change of the
  // applied dates and nothing else.
  const appliedStart = appliedRange.start.getTime();
  const appliedEnd = appliedRange.end.getTime();

  const [draft, setDraft] = useState<Draft>([appliedRange.start, appliedRange.end]);

  useEffect(() => {
    setDraft([new Date(appliedStart), new Date(appliedEnd)]);
  }, [appliedStart, appliedEnd]);

  const handleChange = useCallback(
    (value: Draft | null | undefined) => {
      const start = value?.[0] ?? null;
      const end = value?.[1] ?? null;
      if (!start) {
        setDraft([new Date(appliedStart), new Date(appliedEnd)]);
        return;
      }
      setDraft([start, end]);
      if (end && !(sameDay(start, new Date(appliedStart)) && sameDay(end, new Date(appliedEnd)))) {
        applyPeriod({ start, end });
      }
    },
    [appliedStart, appliedEnd, applyPeriod]
  );

  const handleHide = useCallback(() => {
    setDraft((current) =>
      current?.[0] && current?.[1] ? current : [new Date(appliedStart), new Date(appliedEnd)]
    );
  }, [appliedStart, appliedEnd]);

  return (
    <div className="flex align-items-center gap-2">
      <Calendar
        value={draft}
        onChange={(e) => handleChange(e.value as Draft | null)}
        onHide={handleHide}
        selectionMode="range"
        readOnlyInput
        hideOnRangeSelection
        placeholder="Select date range"
        dateFormat="dd/mm/yy"
        className="w-full sm:w-18rem"
        showIcon
        iconPos="left"
        disabled={disabled}
        ariaLabel="Date range"
      />

      <Button
        icon="pi pi-refresh"
        rounded
        outlined
        severity="secondary"
        tooltip="Reset to the last month"
        tooltipOptions={{ position: "top" }}
        aria-label="Reset to the last month"
        onClick={clearPeriod}
        disabled={disabled}
      />
    </div>
  );
};
